/* Continuous native-scroll lookbook: one compositor transform animation, cached
   geometry and a 5 Hz maintenance timer. No per-frame JavaScript motion loop. */
(function () {
  'use strict';
  if (window.__evEditorialCarousel) return;
  window.__evEditorialCarousel = true;
  const SPEED = 20;
  const MAINTENANCE_DELAY = 200;
  const disposers = new WeakMap();

  function init(root) {
    root.querySelectorAll('[data-collection-editorial]').forEach(gallery => {
      if (gallery.dataset.editorialReady) return;
      gallery.dataset.editorialReady = 'true';
      const track = gallery.querySelector('[data-editorial-track]');
      const rail = gallery.querySelector('[data-editorial-rail]');
      const originals = [...rail.children];
      const imageLoads = new WeakMap();
      const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      let items = [], cycle = 0, step = 0, width = 0;
      let animation = null, timer = null, pointer = null;
      let near = false, visible = false, hovering = false, focused = false;
      let userPaused = false, resumeFocused = false, disposed = false, scheduled = false;
      let revision = 0;

      const supported = () => originals.length >= 2 && !!window.IntersectionObserver && typeof rail.animate === 'function' && !motion?.matches;
      const modulo = value => cycle ? ((value % cycle) + cycle) % cycle : 0;
      const offset = () => modulo(track.scrollLeft + (animation ? (Number(animation.currentTime) || 0) * SPEED / 1000 : 0));
      const canMove = () => !disposed && supported() && visible && !hovering && (!focused || resumeFocused) && !pointer && !userPaused && document.visibilityState !== 'hidden';

      function imageReady(item) {
        const img = item.img;
        if (imageLoads.has(img)) return imageLoads.get(img);
        const ready = new Promise(resolve => {
          const done = success => {
            img.removeEventListener('load', onLoad);
            img.removeEventListener('error', onError);
            if (success && img.decode) img.decode().then(() => resolve(true), () => resolve(true));
            else resolve(success);
          };
          const onLoad = () => done(img.naturalWidth > 0);
          const onError = () => done(false);
          img.addEventListener('load', onLoad);
          img.addEventListener('error', onError);
          img.loading = 'eager';
          if (img.dataset.src) {
            img.srcset = img.dataset.srcset;
            img.src = img.dataset.src;
            delete img.dataset.src;
            delete img.dataset.srcset;
          }
          if (img.complete) done(img.naturalWidth > 0);
        });
        imageLoads.set(img, ready);
        return ready;
      }

      function loadNearby(at, ahead = 0) {
        const visibleItems = items.filter(item => item.left + item.width > at && item.left < at + width + ahead);
        visibleItems.forEach(imageReady);
        const last = items.indexOf(visibleItems.at(-1));
        if (last >= 0 && items[last + 1]) imageReady(items[last + 1]);
        return visibleItems;
      }

      function paint() {
        const at = offset();
        if (near) loadNearby(at);
      }

      function stop(flatten = false) {
        revision += 1;
        if (timer !== null) window.clearTimeout(timer);
        timer = null;
        const at = offset();
        animation?.pause();
        if (flatten && animation) {
          animation.cancel();
          animation = null;
          track.scrollLeft = at;
        }
        return at;
      }

      function arm() {
        if (!canMove() || animation?.playState !== 'running') return;
        if (timer !== null) window.clearTimeout(timer);
        timer = window.setTimeout(maintain, MAINTENANCE_DELAY);
      }

      async function syncMotion(ahead = 0) {
        const at = stop();
        const requestRevision = revision;
        if (!canMove() || !cycle) return;
        const ready = await Promise.all(loadNearby(at, ahead).map(imageReady));
        if (disposed || requestRevision !== revision || !canMove() || ready.some(value => !value)) return;
        if (!animation) {
          animation = rail.animate([{ transform: 'translateX(0px)' }, { transform: `translateX(-${cycle}px)` }], {
            duration: cycle / SPEED * 1000, iterations: Infinity, easing: 'linear'
          });
          animation.pause();
          animation.currentTime = at / SPEED * 1000;
          track.scrollLeft = 0;
        }
        animation.play();
        arm();
      }

      function maintain() {
        timer = null;
        if (!canMove()) { stop(); return; }
        const nearby = loadNearby(offset(), SPEED * MAINTENANCE_DELAY / 1000);
        paint();
        if (nearby.some(item => !item.img.complete || !item.img.naturalWidth)) syncMotion(SPEED * MAINTENANCE_DELAY / 1000);
        else arm();
      }

      function rebuild() {
        const at = stop(true);
        rail.querySelectorAll('[data-editorial-clone]').forEach(clone => clone.remove());
        track.style.removeProperty('--editorial-measured-slide');
        rail.style.width = '';
        width = track.clientWidth;
        const slideWidth = originals[0].offsetWidth;
        const measuredStep = originals[1] ? originals[1].offsetLeft - originals[0].offsetLeft : slideWidth;
        const gap = parseFloat(window.getComputedStyle(rail).columnGap);
        step = slideWidth + (Number.isFinite(gap) ? gap : Math.max(0, measuredStep - slideWidth));
        cycle = step * originals.length;
        if (!step || !width) return;
        if (supported()) {
          const count = Math.ceil(width / step) + 1;
          for (let index = 0; index < count; index++) {
            const original = originals[index % originals.length];
            const clone = original.cloneNode(false);
            const source = original.querySelector('[data-editorial-image]');
            const figure = document.createElement('figure');
            const img = document.createElement('img');
            for (const name of ['width', 'height', 'sizes', 'decoding', 'draggable']) {
              img.setAttribute(name, source.getAttribute(name));
            }
            img.alt = '';
            img.loading = 'lazy';
            img.dataset.editorialImage = '';
            img.dataset.src = source.dataset.src || source.getAttribute('src');
            img.dataset.srcset = source.dataset.srcset || source.getAttribute('srcset');
            img.src = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
            figure.append(img);
            clone.append(figure);
            clone.dataset.editorialClone = 'true';
            clone.setAttribute('aria-hidden', 'true');
            clone.setAttribute('inert', '');
            rail.append(clone);
          }
        }
        track.dataset.continuous = String(supported());
        track.style.setProperty('--editorial-measured-slide', `${slideWidth}px`);
        const children = [...rail.children];
        rail.style.width = `${children.length * step - (step - slideWidth)}px`;
        items = children.map((el, index) => ({ el, img: el.querySelector('[data-editorial-image]'), left: index * step, width: slideWidth }));
        gallery.dataset.editorialCycle = String(cycle);
        gallery.dataset.editorialSpeed = String(SPEED);
        track.scrollLeft = at;
        paint();
        syncMotion();
      }

      function schedulePaint() {
        if (disposed || scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(() => { scheduled = false; paint(); loadNearby(offset()); });
      }

      function manualPause() { userPaused = true; resumeFocused = false; stop(true); }
      track.addEventListener('scroll', () => {
        if (animation && track.scrollLeft) manualPause();
        if (!animation && cycle && track.scrollLeft >= cycle) track.scrollLeft = modulo(track.scrollLeft);
        schedulePaint();
      }, { passive: true });
      gallery.addEventListener('pointerenter', event => { if (event.pointerType === 'touch') return; hovering = true; syncMotion(); });
      gallery.addEventListener('pointerleave', event => { if (event.pointerType === 'touch') return; hovering = false; syncMotion(); });
      gallery.addEventListener('focusin', () => { focused = true; resumeFocused = false; syncMotion(); });
      gallery.addEventListener('focusout', event => { focused = !!event.relatedTarget && track.contains(event.relatedTarget); resumeFocused = false; syncMotion(); });
      track.addEventListener('wheel', event => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) manualPause();
      }, { passive: true });
      track.addEventListener('pointerdown', event => {
        const at = stop(true);
        pointer = { x: event.clientX, y: event.clientY, at, paused: userPaused, type: event.pointerType };
        if (event.pointerType === 'mouse') { event.preventDefault(); track.setPointerCapture?.(event.pointerId); }
      });
      track.addEventListener('pointermove', event => {
        if (pointer?.type === 'mouse' && Math.abs(event.clientX - pointer.x) > 4) {
          track.scrollLeft = Math.max(0, pointer.at + pointer.x - event.clientX);
          schedulePaint();
        }
      });
      track.addEventListener('pointerup', event => {
        if (!pointer) return;
        const dragged = Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) > 8;
        userPaused = dragged ? pointer.paused : !pointer.paused;
        resumeFocused = !userPaused;
        pointer = null;
        if (track.hasPointerCapture?.(event.pointerId)) track.releasePointerCapture(event.pointerId);
        syncMotion();
      });
      track.addEventListener('pointercancel', () => { pointer = null; manualPause(); });
      track.addEventListener('keydown', event => {
        if (event.target !== track) return;
        if (event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          userPaused = !userPaused; resumeFocused = !userPaused; syncMotion(); return;
        }
        if (event.key === 'Escape') { event.preventDefault(); manualPause(); return; }
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const current = Math.round(offset() / step) % originals.length;
        manualPause();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? originals.length - 1 : Math.max(0, Math.min(originals.length - 1, current + (event.key === 'ArrowRight' ? 1 : -1)));
        imageReady(items[next]);
        track.scrollTo({ left: items[next].left, behavior: motion?.matches || event.key === 'Home' || event.key === 'End' ? 'instant' : 'smooth' });
        paint(); loadNearby(offset());
      });

      const observer = window.IntersectionObserver ? new IntersectionObserver(entries => {
        near = entries[0].isIntersecting;
        visible = near && entries[0].intersectionRatio >= .15;
        paint(); syncMotion();
      }, { threshold: [0, .15] }) : null;
      const resize = window.ResizeObserver ? new ResizeObserver(rebuild) : null;
      rebuild();
      observer?.observe(gallery);
      resize?.observe(track);
      if (!resize) window.addEventListener('resize', rebuild);
      const visibilityChanged = () => syncMotion();
      document.addEventListener('visibilitychange', visibilityChanged);
      motion?.addEventListener?.('change', rebuild);

      disposers.set(gallery, () => {
        disposed = true;
        stop();
        animation?.cancel();
        observer?.disconnect();
        resize?.disconnect();
        window.removeEventListener('resize', rebuild);
        document.removeEventListener('visibilitychange', visibilityChanged);
        motion?.removeEventListener?.('change', rebuild);
      });
    });
  }
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-collection-editorial]').forEach(gallery => disposers.get(gallery)?.());
  });
}());
